import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import useApi from 'hooks/useApi';
import useSelection from 'hooks/useSelection';
import {
  Announcement,
  AnnouncementAttachment,
  AnnouncementKind,
  ANNOUNCEMENT_KIND_LABELS,
  CreateAnnouncementPayload,
  UpdateAnnouncementPayload
} from 'types/auth';
import AttachmentUploader from './AttachmentUploader';
import AudienceSelector, { AudienceValue } from './AudienceSelector';

const TITLE_MAX = 200;

interface AnnouncementFormDialogProps {
  open: boolean;
  // null = create, otherwise edit that announcement.
  announcement: Announcement | null;
  onClose: () => void;
  onSaved: (announcement: Announcement) => void;
}

interface FormErrors {
  title?: string;
  description?: string;
  audience?: string;
}

const AnnouncementFormDialog = ({ open, announcement, onClose, onSaved }: AnnouncementFormDialogProps) => {
  const { createAnnouncement, updateAnnouncement, showToast } = useApi();
  const { clientOptions } = useSelection();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<AnnouncementKind>('announcement');
  // The four audience fields travel as one object: they are interdependent, and the
  // server rewrites all four together whenever any of them changes. Splitting them into
  // four useStates would invite an update that moves one without the others.
  const [audience, setAudience] = useState<AudienceValue>({ clients: [], allClients: false, process: null, roles: [] });
  const [attachments, setAttachments] = useState<AnnouncementAttachment[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(announcement);

  // Reset on every open so a cancelled edit never bleeds into the next one.
  useEffect(() => {
    if (!open) return;
    setTitle(announcement?.title ?? '');
    setDescription(announcement?.description ?? '');
    setType(announcement?.type ?? 'announcement');
    setAudience({
      clients: (announcement?.clients ?? []).filter((client) => client?.uid).map((client) => client.uid),
      allClients: announcement?.allClients ?? false,
      process: announcement?.process?.uid ?? null,
      roles: (announcement?.roles ?? []).filter((role) => role?.uid).map((role) => role.uid)
    });
    // Drop attachments that are neither an upload (file_path) nor a link (public_url).
    // Those can only come from records written before AnnouncementAttachment had its
    // @Schema() decorator, when Mongoose silently discarded every subdocument field —
    // sending them back would fail Joi and block the edit entirely.
    setAttachments(
      (announcement?.attachments ?? []).filter((attachment) => attachment?.file_path || (attachment?.is_link && attachment?.public_url))
    );
    setErrors({});
  }, [open, announcement]);

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!title.trim()) next.title = 'Title is required';
    else if (title.trim().length > TITLE_MAX) next.title = `Title cannot exceed ${TITLE_MAX} characters`;
    if (!description.trim()) next.description = 'Description is required';
    if (!audience.allClients && audience.clients.length === 0) {
      next.audience = 'Select at least one client, or tick All clients';
    }
    // The server enforces this too. Catching it here turns a rejected save into an inline
    // message on the field that caused it, which is the difference between "fix this" and
    // "something went wrong".
    else if (audience.process && audience.roles.length === 0) {
      next.audience = 'Select at least one role for the chosen process';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // `url` is a short-lived signed URL held only for the preview — the API rejects it.
    // `media_type` is normally derived server-side from the mime type, so it goes too —
    // except on a pasted link, which has no mime type and states its own kind.
    const payloadAttachments = attachments.map(({ url, media_type, ...rest }) => (rest.is_link ? { ...rest, media_type } : rest));

    const payload: CreateAnnouncementPayload | UpdateAnnouncementPayload = {
      title: title.trim(),
      description: description.trim(),
      type,
      allClients: audience.allClients,
      // A broadcast supersedes any explicit selection, and the server clears the list
      // anyway — send an empty array so the two never disagree.
      clients: audience.allClients ? [] : audience.clients,
      // Always sent, both of them, even when null/empty. The server rewrites the whole
      // audience whenever any part of it appears, so omitting these on an edit that
      // widened the client selection would leave the old process attached to the new
      // client — which it would then reject. Sending the audience whole is the contract.
      process: audience.allClients ? null : audience.process,
      roles: audience.allClients ? [] : audience.roles,
      attachments: payloadAttachments
    };

    setSaving(true);
    try {
      const response = isEdit
        ? await updateAnnouncement(announcement!.uid, payload)
        : await createAnnouncement(payload as CreateAnnouncementPayload);

      if (response?.success) {
        showToast(isEdit ? 'Announcement updated' : 'Announcement published', 'success');
        onSaved(response.data);
      } else {
        showToast(response?.message || 'Could not save the announcement', 'error');
      }
    } catch (error: any) {
      showToast(error?.message || 'Could not save the announcement', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit announcement' : 'New announcement'}</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* First field rather than an afterthought under the body. It frames the title
              and description the author is about to write, and it is the only field here
              carrying a silent default — putting it last would let it be missed. */}
          <TextField
            select
            label="Type"
            value={type}
            onChange={(event) => setType(event.target.value as AnnouncementKind)}
            helperText="Tagged in the list, and filterable there. Does not change who sees this."
            disabled={saving}
            fullWidth
          >
            {(Object.keys(ANNOUNCEMENT_KIND_LABELS) as AnnouncementKind[]).map((kind) => (
              <MenuItem key={kind} value={kind}>
                {ANNOUNCEMENT_KIND_LABELS[kind]}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            error={Boolean(errors.title)}
            helperText={errors.title ?? `${title.length}/${TITLE_MAX}`}
            disabled={saving}
            fullWidth
            autoFocus
          />

          {/* Stored as plain text; the detail view splits it on newlines and renders one
              bullet per line, so the line breaks the author types are the list they get. */}
          <TextField
            label="Description"
            placeholder={'Record-breaking growth in APAC and EMEA regions.\nNew operational efficiencies across logistics hubs.'}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            error={Boolean(errors.description)}
            helperText={errors.description ?? 'One bullet point per line — press Enter to start a new one.'}
            disabled={saving}
            multiline
            minRows={5}
            fullWidth
          />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Attachments</Typography>
            <AttachmentUploader value={attachments} onChange={setAttachments} disabled={saving} />
          </Stack>

          <Stack spacing={1}>
            <Typography variant="subtitle2">Who sees this</Typography>
            <AudienceSelector
              clientOptions={clientOptions}
              value={audience}
              onChange={(next) => {
                setAudience(next);
                setErrors((prev) => ({ ...prev, audience: undefined }));
              }}
              disabled={saving}
              error={Boolean(errors.audience)}
              helperText={errors.audience}
            />
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="secondary" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isEdit ? 'Save changes' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementFormDialog;
